<script setup>
import {computed, ref, watch, nextTick} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRoute} from 'vue-router'
import SunCalc from 'suncalc'
import {use} from 'echarts/core'
import {CanvasRenderer} from 'echarts/renderers'
import {LineChart} from 'echarts/charts'
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import * as echarts from 'echarts'
import Loader from '@/views/Loader.vue'
import axios from '@/helpers/axios'
import moment from 'moment'
import 'moment/locale/pt'

definePage({
  meta: {
    layout: 'default',
    role: 'user',
  },
})

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
])

const { t, locale } = useI18n()
const route = useRoute()

const uuid = route.params.uuid
const channelUuid = route.params.channel

const DATE_FORMAT = 'YYYY-MM-DD HH:mm'
const DATE_RANGE_FORMAT = 'YYYY-MM-DD'
const DAY_AREA_COLOR = 'rgba(247, 184, 0, 0.3)'
const DAY_LABEL = '☀️'

const device = ref({})
const channel = ref({})
const dates = ref([])
const values = ref([])
const stats = ref(null)
const bread = ref([])

const ready = ref(false)
const loading = ref(false)
const loadingStats = ref(false)
const isCumulative = ref(false) // ✅ enable cumulative chart

const startDate = ref(moment().subtract(7, 'days').format(DATE_RANGE_FORMAT))
const endDate = ref(moment().format(DATE_RANGE_FORMAT))

const quickRangeOptions = [
  { value: 'today', labelKey: 'devices.range_today' },
  { value: '24h', labelKey: 'devices.range_24h' },
  { value: '3d', labelKey: 'devices.range_3d' },
  { value: '7d', labelKey: 'devices.range_7d' },
  { value: '30d', labelKey: 'devices.range_30d' },
  { value: 'custom', labelKey: 'devices.range_custom' },
]

const quickRange = ref('7d')
let isApplyingQuickRange = false
let skipNextFetch = false

const resolveQuickRangeDates = type => {
  const now = moment()
  let startMoment = now.clone().subtract(7, 'days')

  switch (type) {
    case 'today':
      startMoment = now.clone().startOf('day')
      break
    case '24h':
      startMoment = now.clone().subtract(1, 'day')
      break
    case '3d':
      startMoment = now.clone().subtract(3, 'days')
      break
    case '7d':
      startMoment = now.clone().subtract(7, 'days')
      break
    case '30d':
      startMoment = now.clone().subtract(30, 'days')
      break
    default:
      startMoment = now.clone().subtract(7, 'days')
  }

  return {
    start: startMoment.format(DATE_RANGE_FORMAT),
    end: now.format(DATE_RANGE_FORMAT),
  }
}

moment.locale(locale.value || 'en')

watch(
  () => locale.value,
  newLocale => {
    moment.locale(newLocale || 'en')
  }
)

const lastReadingFormatted = computed(() => {
  if (!device.value || !device.value.last_data) return null

  return moment.utc(device.value.last_data).local().format('YYYY-MM-DD HH:mm')
})

const transducerReference = computed(
  () => channel.value?.transducer?.reference || null
)

const cumulativeValues = computed(() => {
  if (!isCumulative.value) return values.value
  return values.value.reduce((acc, val, i) => {
    acc.push(val + (acc[i - 1] || 0))
    return acc
  }, [])
})

const chartSeriesData = computed(() => {
  if (!dates.value.length || !cumulativeValues.value.length) return []

  const points = []
  const limit = Math.min(dates.value.length, cumulativeValues.value.length)

  for (let i = 0; i < limit; i++) {
    const dateString = dates.value[i]
    const value = cumulativeValues.value[i]

    if (dateString === undefined || value === undefined) continue

    const parsed = moment(dateString, DATE_FORMAT)
    if (!parsed.isValid()) continue

    points.push([parsed.valueOf(), value])
  }

  return points
})

const dayNightMarkAreas = computed(() => {
  const lat = Number(device.value?.last_location?.latitude)
  const lng = Number(device.value?.last_location?.longitude)

  if (
    !dates.value.length ||
    Number.isNaN(lat) ||
    Number.isNaN(lng)
  )
    return []

  const minMoment = moment(dates.value[0], DATE_FORMAT)
  const maxMoment = moment(dates.value[dates.value.length - 1], DATE_FORMAT)
  const nowMoment = moment()

  if (!minMoment.isValid() || !maxMoment.isValid()) return []

  const clampMaxMoment =
    maxMoment.isSame(nowMoment, 'day') && nowMoment.isAfter(maxMoment)
      ? nowMoment
      : maxMoment

  const ranges = []

  const pushArea = (start, end) => {
    const clampedStart = moment.max(start, minMoment)
    const clampedEnd = moment.min(end, clampMaxMoment)

    if (clampedStart.isSameOrAfter(clampedEnd)) return

    ranges.push([
      { xAxis: clampedStart.valueOf() },
      { xAxis: clampedEnd.valueOf() },
    ])
  }

  for (
    let cursor = minMoment.clone().startOf('day');
    cursor.isSameOrBefore(clampMaxMoment, 'day');
    cursor = cursor.add(1, 'day')
  ) {
    const times = SunCalc.getTimes(cursor.toDate(), lat, lng)
    const sunrise = moment(times.sunrise)
    const sunset = moment(times.sunset)

    if (!sunrise.isValid() || !sunset.isValid() || !sunrise.isBefore(sunset)) continue

    let daylightEnd = sunset

    if (cursor.isSame(nowMoment, 'day') && nowMoment.isBefore(daylightEnd))
      daylightEnd = nowMoment

    if (cursor.isSame(maxMoment, 'day') && maxMoment.isBefore(daylightEnd))
      daylightEnd = maxMoment

    pushArea(sunrise, daylightEnd)
  }

  return ranges
})

// --- Fetch device ---
axios
  .get(`/devices/${uuid}`)
  .then(response => (device.value = response.data))
  .catch(console.error)
  .finally(() => (ready.value = true))

// --- Fetch channel ---
axios
  .get(`/devices/${uuid}/${channelUuid}`)
  .then(response => (channel.value = response.data))
  .catch(console.error)
  .finally(() => (ready.value = true))


const getStats = async () => {
  loadingStats.value = true
  try {
    const response = await axios.get(`/devices/${uuid}/${channelUuid}/stats`, {
      params: {
        start: startDate.value,
        end: endDate.value,
      },
    })


    stats.value = response.data
  } catch (e) {
    console.error(e)
  } finally {
    loadingStats.value = false
  }
}

// --- Fetch data ---
const getData = async () => {
  loading.value = true
  try {
    const response = await axios.get(`/devices/${uuid}/${channelUuid}/data`, {
      params: {
        start: startDate.value,
        end: endDate.value,
      },
    })

    dates.value = response.data.dates.map(date =>
      moment.utc(date[0]).local().format(DATE_FORMAT)
    )
    values.value = response.data.values

    getStats()
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

getData()

// --- React when date range changes ---
watch([startDate, endDate], ([newStart, newEnd]) => {
  if (!newStart || !newEnd) return

  const start = moment(newStart, DATE_RANGE_FORMAT)
  const end = moment(newEnd, DATE_RANGE_FORMAT)

  if (start.isAfter(end)) {
    startDate.value = end.format(DATE_RANGE_FORMAT)
    return
  }

  if (!isApplyingQuickRange && quickRange.value !== 'custom') quickRange.value = 'custom'

  if (skipNextFetch) return

  getData()
})

watch(quickRange, newValue => {
  if (!newValue || newValue === 'custom') return

  const { start, end } = resolveQuickRangeDates(newValue)
  isApplyingQuickRange = true
  skipNextFetch = true
  startDate.value = start
  endDate.value = end

  nextTick(() => {
    isApplyingQuickRange = false
    skipNextFetch = false
    getData()
  })
})

// --- ECharts options ---
const options = computed(() => {
  const seriesConfig = {
    name: channel.value.name,
    data: chartSeriesData.value,
    type: 'line',
    symbol: 'none',
    sampling: 'lttb',
    itemStyle: { color: 'rgb(0, 175, 54)' },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgb(0, 175, 54)' },
        { offset: 1, color: 'rgb(102, 242, 157)' },
      ]),
    },
  }

  if (dayNightMarkAreas.value.length) {
    seriesConfig.markArea = {
      silent: true,
      itemStyle: { color: DAY_AREA_COLOR },
      label: {
        show: true,
        color: '#c47f00',
        fontWeight: 'bold',
        formatter: DAY_LABEL,
      },
      data: dayNightMarkAreas.value,
    }
  }

  const points = chartSeriesData.value
  const firstPointTs = points.length ? points[0][0] : undefined
  const lastPointTs = points.length ? points[points.length - 1][0] : undefined
  const nowTs = moment().valueOf()
  const extendToNow =
    lastPointTs && moment(lastPointTs).isSame(nowTs, 'day') && nowTs > lastPointTs
  const xAxisMax = extendToNow ? nowTs : lastPointTs

  return {
    tooltip: {
      trigger: 'axis',
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      min: firstPointTs,
      max: xAxisMax,
    },
    yAxis: {
      type: 'value',
      boundaryGap: [0, '100%'],
      min: 'dataMin',
      max: 'dataMax',
    },
    dataZoom: [
      { type: 'inside', start: 0, end: 100 },
      { start: 0, end: 100 },
    ],
    series: [seriesConfig],
  }
})

</script>

<template>
  <div>
    <Loader v-if="!ready" />
    <VCard
      v-else
      class="overflow-visible pa-4"
      style="border-radius: 16px;"
      elevation="2"
    >
      <!-- Header -->
      <div class="d-flex justify-space-between align-start mb-4">
        <div>
          <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
            <RouterLink
              class="text-high-emphasis text-decoration-none text-primary"
              :to="{ name: 'devices-uuid', params: { uuid: device.uuid } }"
            >
              {{ device.name }}
            </RouterLink>
            <span> - {{ channel.name }}</span>
          </h4>
          <p class="text-body-1 text-medium-emphasis mb-0 d-inline-flex gap-2">
            <template v-if="transducerReference || lastReadingFormatted">
              <span
                v-if="transducerReference"
                class="font-weight-medium d-inline-flex align-center gap-1"
              >
                <VIcon icon="tabler-cpu" size="small" />
                {{ transducerReference }}
              </span>
              <span
                v-if="lastReadingFormatted"
                class="d-inline-flex align-center gap-1"
              >
                <VIcon icon="tabler-access-point" size="small" />
                {{ lastReadingFormatted }}
              </span>
            </template>
            <template v-else>
              {{ $t('devices.no_data') }}
            </template>
          </p>
        </div>

        <VBtn
          icon
          size="small"
          variant="tonal"
          color="primary"
          :to="{ name: 'devices-uuid', params: { uuid: device.uuid } }"
        >
          <VIcon icon="tabler-arrow-left" size="x-large" />
          <VTooltip activator="parent" location="top">
            {{ $t('layout.go_back') }}
          </VTooltip>
        </VBtn>
      </div>

      <!-- Stats Section -->
      <VCardText
        class="mb-2"
        v-if="stats && channel"
        :class="{ 'stats-loading': loadingStats }"
      >
        <VRow>
          <VCol cols="6" sm="6" md="3">
            <VCard
              variant="tonal"
              color="success"
              class="pa-3 text-center"
              style="border-radius: 12px;"
            >
              <p class="text-subtitle-2 text-medium-emphasis mb-1">{{ $t('devices.average') }}</p>
              <h3 class="text-h5 font-weight-semibold text-success">{{ stats.average }}<span v-if="channel.transducer.unit">{{' ' + channel.transducer.unit}}</span></h3>
            </VCard>
          </VCol>

          <VCol cols="6" sm="6" md="3">
            <VCard
              variant="tonal"
              color="red"
              class="pa-3 text-center"
              style="border-radius: 12px;"
            >
              <p class="text-subtitle-2 text-medium-emphasis mb-1">{{ $t('devices.maximum') }}</p>
              <h3 class="text-h5 font-weight-semibold text-red">{{ stats.max }}<span v-if="channel.transducer.unit">{{' ' + channel.transducer.unit}}</span></h3>
            </VCard>
          </VCol>

          <VCol cols="6" sm="6" md="3">
            <VCard
              variant="tonal"
              color="blue"
              class="pa-3 text-center"
              style="border-radius: 12px;"
            >
              <p class="text-subtitle-2 text-medium-emphasis mb-1">{{ $t('devices.minimum') }}</p>
              <h3 class="text-h5 font-weight-semibold text-blue">{{ stats.min }}<span v-if="channel.transducer.unit">{{' ' + channel.transducer.unit}}</span></h3>
            </VCard>
          </VCol>

          <VCol cols="6" sm="6" md="3">
            <VCard
              variant="tonal"
              color="purple"
              class="pa-3 text-center"
              style="border-radius: 12px;"
            >
              <p class="text-subtitle-2 text-medium-emphasis mb-1">{{ $t('devices.amplitude') }}</p>
              <h3 class="text-h5 font-weight-semibold text-purple">{{ stats.amplitude }}<span v-if="channel.transducer.unit">{{' ' + channel.transducer.unit}}</span></h3>
            </VCard>
          </VCol>
        </VRow>
      </VCardText>

      <!-- Date pickers -->
      <VCardText>
        <div class="quick-range-wrapper">
          <p class="text-subtitle-2 text-medium-emphasis mb-0">
            {{ $t('devices.quick_range_title') }}
          </p>

          <VBtnToggle
            v-model="quickRange"
            mandatory
            rounded="pill"
            class="quick-range-toggle"
          >
            <VBtn
              v-for="option in quickRangeOptions"
              :key="option.value"
              :value="option.value"
              variant="text"
              size="small"
              rounded="pill"
              :class="['quick-range-btn', { 'quick-range-btn--active': quickRange === option.value }]"
            >
              {{ $t(option.labelKey) }}
            </VBtn>
          </VBtnToggle>
        </div>

        <VExpandTransition>
          <div v-show="quickRange === 'custom'" class="mt-4">
            <VForm @submit.prevent>
              <VRow class="custom-range-grid">
                <VCol cols="12" md="6">
                  <AppDateTimePicker
                    v-model="startDate"
                    :label="$t('devices.start_date')"
                    density="compact"
                    class="custom-range-picker"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <AppDateTimePicker
                    v-model="endDate"
                    :label="$t('devices.end_date')"
                    density="compact"
                    class="custom-range-picker"
                  />
                </VCol>
              </VRow>
            </VForm>
          </div>
        </VExpandTransition>
      </VCardText>

      <!-- Cumulative toggle -->
      <VCardText>
        <VSwitch
          v-model="isCumulative"
          color="primary"
        >
          <template #label>
            <span class="d-inline-flex align-center gap-1">
              <span>
                {{ isCumulative ? $t('devices.cumulative') : $t('devices.raw_data') }}
              </span>
              <span
                v-if="!isCumulative"
                class="d-inline-flex align-center text-medium-emphasis"
              >
                <VIcon icon="tabler-info-circle" size="small" />
                <VTooltip activator="parent" location="top">
                  {{ $t('devices.raw_data_info') }}
                </VTooltip>
              </span>
            </span>
          </template>
        </VSwitch>
      </VCardText>

      <!-- Chart -->
      <VCardText>
        <Loader v-if="loading" />
        <VChart
          v-show="!loading"
          style="height: 500px"
          :option="options"
          autoresize
        />
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.quick-range-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  text-align: center;
}

.quick-range-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  border-radius: 999px;
  padding: 0.35rem;
  background-color: rgba(var(--v-theme-surface), 0.75);
  box-shadow: 0 5px 20px rgba(15, 23, 42, 0.08);
}

.quick-range-btn {
  border-radius: 999px;
  text-transform: none;
  font-weight: 600;
  padding-inline: 1.25rem;
}

.quick-range-btn--active {
  background-color: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  box-shadow: 0 6px 14px rgba(var(--v-theme-primary), 0.2);
}

.stats-loading {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}


.custom-range-grid {
  max-width: 640px;
  margin-inline: auto;
  row-gap: 1rem;
}

.custom-range-picker {
  width: 100%;
}

.custom-range-picker :deep(.v-field) {
  border-radius: 14px;
  background-color: rgba(var(--v-theme-surface-variant), 0.6);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.custom-range-picker :deep(.v-field--focused) {
  box-shadow: 0 6px 20px rgba(var(--v-theme-primary), 0.15);
}
</style>
